import classnames from 'classnames';
import { useEffect, type FunctionComponent, type ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';

import { Carousel } from '@/core/tamagoshi/Carousel/Carousel';
import { useBreakpoints } from '@/core/tamagoshi/MediaQuery/MediaQuery';
import type {
  ProgrammaticAdviceRecoLevel,
  ProgrammaticAdviceRecoProduct,
} from '@/digitalAdvice/Recommendation/ProgrammaticAdviceReco.model';
import { ProgrammaticAdviceRecoHeader } from '@/domains/digitalAdvice/Recommendation/components/ProgrammaticAdviceRecoHeader/ProgrammaticAdviceRecoHeader';
import { ProgrammaticAdviceRecoItem } from '@/domains/digitalAdvice/Recommendation/components/ProgrammaticAdviceRecoItem/ProgrammaticAdviceRecoItem';
import { Tracking } from '@/domains/digitalAdvice/Recommendation/tracking';
import { usePlaylistReconciliation } from '@/domains/productDiscovery/Recommendation/modules/ProductRecommendation/hooks/usePlaylistReconciliation';

import styles from './ProgrammaticAdviceReco.module.scss';

export interface ProgrammaticAdviceRecoProps {
  categoryName?: string;
  className?: string;
  products: [ProgrammaticAdviceRecoLevel, ProgrammaticAdviceRecoProduct][];
}

const engineName = 'PAR';
const eventTrigger = 'listing';
export const ProgrammaticAdviceReco: FunctionComponent<
  ProgrammaticAdviceRecoProps
> = ({ categoryName, className, products }) => {
  const { createPlaylistReconciliation, persistPlaylistReconciliation } =
    usePlaylistReconciliation();

  const [root, rootInView] = useInView({ threshold: 0.85, triggerOnce: true });

  const { isSizeAbove } = useBreakpoints();
  const isDesktop = isSizeAbove('lg');

  useEffect(() => {
    if (rootInView) {
      Tracking.onDisplay(
        categoryName,
        products,
        `${engineName}-${eventTrigger}`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootInView, categoryName]);

  const handleSlideChange = (
    currentPosition: number,
    previousPosition: number,
  ) => {
    Tracking.onSlideChange(
      currentPosition,
      currentPosition < previousPosition ? 'left' : 'right',
      products,
    );
  };

  const items: ReactNode[] = products.map(([level, product]) => (
    <ProgrammaticAdviceRecoItem
      categoryName={categoryName}
      className={styles.product}
      inCarousel={!isDesktop}
      key={`recommendation-${product.modelId}`}
      level={level}
      product={product}
      onBodyClick={() => {
        createPlaylistReconciliation(engineName, eventTrigger).then(
          (result) => {
            persistPlaylistReconciliation(
              result.playlistFingerprint,
              result.playlistReconciliation,
              product.productId.toString(),
            );
          },
        );
      }}
    />
  ));

  const children: ReactNode = isDesktop ? (
    <>
      <ProgrammaticAdviceRecoHeader className={styles.header} />
      <div className={styles.body} data-testid="Regular">
        {items}
      </div>
    </>
  ) : (
    <Carousel
      className={styles.carousel}
      dataTestId="Carousel"
      horizontalPadding="md"
      noControls
      onSlideChange={handleSlideChange}
      sliderClassName={styles.carouselSlider}
      title={<ProgrammaticAdviceRecoHeader />}
      titleClassName={styles.header}
    >
      {items}
    </Carousel>
  );

  return (
    <div className={classnames(styles.root, className)} ref={root}>
      {children}
    </div>
  );
};

ProgrammaticAdviceReco.displayName = 'ProgrammaticAdviceReco';
